import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ICandidate, IJob } from 'wisestep-api';
import { Button, ControlledSalaryDetails, Label, WsDrawer } from 'wisestep-base';

import ActionFooter from '../../features/conversationWidget/components/ActionFooter';
import { submissionFormDefaultValues } from '../../features/conversationWidget/utils/fieldDefaultValues';
import { updateSubmission } from '../../features/conversationWidget/utils/saveSubmission';
import { success, failed } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';

interface IUpdateSalaryModal {
  onClose: () => void;
  job?: IJob | null;
  interaction: ICandidate;
  updateInteraction?: (interaction: ICandidate) => void;
}

const UpdateSalaryModal = ({
  onClose,
  job,
  interaction,
  updateInteraction = () => {},
}: IUpdateSalaryModal) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const formOptions = {
    defaultValues: submissionFormDefaultValues(interaction),
  };
  const methods = useForm(formOptions);
  const { handleSubmit, reset } = methods;
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const isPermanent = interaction?.jobType?.toLowerCase() === 'permanent';

  const onSubmit = (data: FieldValues) => update(data, interaction);

  const update = async (data: FieldValues, candidate: ICandidate) => {
    setDisableSubmit(true);
    const response = await updateSubmission(
      data,
      { ...candidate, editSubmissionDetails: true },
      [],
      null,
      job
    );
    if (response?.[0]) {
      setNotification(success('Submission Update'));
      updateInteraction({ ...candidate, ...response[0] });
      onClose();
    } else {
      setNotification(failed('Update failed'));
      setDisableSubmit(false);
    }
  };

  useEffect(() => {
    reset(submissionFormDefaultValues(interaction));
  }, [interaction, reset]);

  return (
    <>
      <WsDrawer open={true} onClose={onClose} size="small" title={'Update Salary'}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container direction="row" spacing={2} xs={12} alignItems="center" p={4}>
              <Grid item xs={12}>
                <Label>{'Current Salary'}</Label>
                <ControlledSalaryDetails name="currentSalary" />
              </Grid>
              <Grid item xs={12}>
                <Label>{isPermanent ? 'Submitted Compensation' : 'Submitted Pay Rate'}</Label>
                <ControlledSalaryDetails name="expectedSalary" />
              </Grid>
              <Grid item xs={12}>
                <Label>{isPermanent ? 'Offered Salary' : 'Offered Pay Rate'}</Label>
                <ControlledSalaryDetails name="offeredPayRate" />
              </Grid>
              {interaction?.jobType?.toLowerCase() !== 'permanent' && (
                <>
                  <Grid item xs={12}>
                    <Label>{'Client Bill Rate'}</Label>
                    <ControlledSalaryDetails name="clientBillRate" />
                  </Grid>
                  <Grid item xs={12}>
                    <Label>{'Pay Rate Expenses'}</Label>
                    <ControlledSalaryDetails name="payRateExpenses" />
                  </Grid>
                </>
              )}
            </Grid>
            <ActionFooter>
              <Grid container xs={3.5} display={'flex'} justifyContent={'end'}>
                <Button variant="contained" type="submit" disabled={disableSubmit}>
                  Update
                </Button>
              </Grid>
            </ActionFooter>
          </form>
        </FormProvider>
      </WsDrawer>
    </>
  );
};

export default UpdateSalaryModal;
